import React, {useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import { Colors, Fonts } from '../utils/colors';

export type PromptSelectOption<T extends string | number> = {
  value: T
  label: string
}

export function PromptSelect<T extends string | number>(props: {
  label?: string
  value: T
  options: PromptSelectOption<T>[]
  onChange: (value: T) => void
}) {
  const [isOpen, setIsOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const selected = props.options.find(option => option.value === props.value)
  const selectedIndex = props.options.findIndex(option => option.value === props.value)

  useEffect(() => {
    if (!isOpen) {
      return
    }
    const handlePointerDown = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false)
      } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault()
        const step = event.key === "ArrowDown" ? 1 : -1
        const nextIndex = Math.max(0, Math.min(
          props.options.length - 1,
          (selectedIndex < 0 ? 0 : selectedIndex) + step
        ))
        props.onChange(props.options[nextIndex].value)
      } else if (event.key === "Enter") {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", handlePointerDown)
    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("mousedown", handlePointerDown)
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [isOpen, props, selectedIndex])

  return (
    <Wrap ref={wrapRef}>
      {props.label && <Label>{props.label}</Label>}
      <Trigger
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Caret>&gt;</Caret>
        <Value>{selected ? selected.label : ""}</Value>
        <Chevron $open={isOpen}>v</Chevron>
      </Trigger>
      {isOpen && (
        <Menu role="listbox" aria-label={props.label}>
          {props.options.map(option => (
            <Option
              key={String(option.value)}
              type="button"
              role="option"
              aria-selected={option.value === props.value}
              $selected={option.value === props.value}
              onClick={() => {
                props.onChange(option.value)
                setIsOpen(false)
              }}
            >
              <Caret>{option.value === props.value ? ">" : " "}</Caret>
              {option.label}
            </Option>
          ))}
        </Menu>
      )}
    </Wrap>
  )
}

const Wrap = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-family: ${Fonts.mono};
`;

const Label = styled.span`
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${Colors.terminalMuted};
`;

const Trigger = styled.button`
  appearance: none;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${Colors.terminalBorder};
  background: ${Colors.fieldBg};
  color: ${Colors.terminalText};
  font-family: ${Fonts.mono};
  font-size: 14px;
  text-align: left;
  cursor: pointer;

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: ${Colors.destructGreen};
    box-shadow: 0 0 0 1px ${Colors.accentFocus};
  }
`;

const Caret = styled.span`
  flex-shrink: 0;
  width: 1ch;
  color: ${Colors.destructGreen};
  text-shadow: 0 0 8px ${Colors.terminalGlow};
  user-select: none;
`;

const Value = styled.span`
  flex: 1;
  min-width: 0;
`;

const Chevron = styled.span<{$open: boolean}>`
  color: ${Colors.terminalMuted};
  transform: ${props => props.$open ? "rotate(180deg)" : "none"};
  transition: transform 0.15s ease;
`;

const Menu = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  z-index: 5;
  border: 1px solid ${Colors.destructGreen};
  background: ${Colors.terminalPanel};
  box-shadow: ${Colors.menuShadow};
`;

const Option = styled.button<{$selected: boolean}>`
  appearance: none;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: 0;
  background: ${props => props.$selected ? Colors.accentSoft : "transparent"};
  color: ${props => props.$selected ? Colors.destructGreen : Colors.terminalText};
  font-family: ${Fonts.mono};
  font-size: 14px;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${Colors.accentWash};
    color: ${Colors.destructGreen};
  }
`;
